import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  PLATFORM_ID,
  Inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ProductService } from "../../services/product.service";
import { Product } from "../../classes/product";
import { CartAuthGateService } from '../../../core/services/cart-auth-gate.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, AfterViewInit, OnDestroy {

  public products: Product[] = [];
  public search: boolean = false;
  public searchTerm = '';

  @ViewChild('searchOverlay') searchOverlayRef!: ElementRef<HTMLElement>;

  private searchOverlayOriginalParent?: HTMLElement | null;
  private readonly _searchInput$ = new Subject<string>();
  private readonly _destroy$ = new Subject<void>();

  public languages = [{
    name: 'English',
    code: 'en'
  }, {
    name: 'French',
    code: 'fr'
  }];

  public currencies = [{
    name: 'Euro',
    currency: 'EUR',
    price: 0.90
  }, {
    name: 'Rupees',
    currency: 'INR',
    price: 70.93
  }, {
    name: 'Pound',
    currency: 'GBP',
    price: 0.78
  }, {
    name: 'Dollar',
    currency: 'USD',
    price: 1
  }];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private translate: TranslateService,
    private readonly router: Router,
    public productService: ProductService,
    private readonly cartAuthGate: CartAuthGateService
  ) {
    this.productService.cartItems.subscribe(response => this.products = response);
  }

  ngOnInit(): void {
    this.syncSearchTermFromUrl();
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this._destroy$)
    ).subscribe(() => this.syncSearchTermFromUrl());

    this._searchInput$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this._destroy$)
    ).subscribe((term) => {
      if (!this.search) {
        return;
      }
      this.navigateToCatalog(term, true);
    });
  }

  private syncSearchTermFromUrl(): void {
    const busqueda = this.router.parseUrl(this.router.url).queryParams['busqueda'];
    if (busqueda) {
      this.searchTerm = String(busqueda);
    }
  }

  ngAfterViewInit(): void {
    if (this.searchOverlayRef?.nativeElement?.parentElement) {
      this.searchOverlayOriginalParent = this.searchOverlayRef.nativeElement.parentElement;
    }
  }

  searchToggle(): void {
    this.search = !this.search;
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const el = this.searchOverlayRef?.nativeElement;
    if (!el) {
      return;
    }

    if (this.search) {
      this.searchOverlayOriginalParent =
        this.searchOverlayOriginalParent || el.parentElement;
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      document.documentElement.style.overflow = 'hidden';
    } else {
      const parent = this.searchOverlayOriginalParent;
      if (parent && el.parentElement === document.body) {
        parent.appendChild(el);
      }
      document.documentElement.style.overflow = '';
    }
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.searchTerm = value;
    this._searchInput$.next(value);
  }

  onSearchSubmit(event?: Event): void {
    event?.preventDefault();
    this.navigateToCatalog(this.searchTerm, true);
    if (this.search) {
      this.searchToggle();
    }
  }

  private navigateToCatalog(term: string, resetPage: boolean): void {
    const busqueda = term?.trim();
    this.router.navigate(['/shop/collection/left/sidebar'], {
      queryParams: {
        busqueda: busqueda || null,
        ...(resetPage ? { page: 1 } : {})
      },
      queryParamsHandling: 'merge'
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.documentElement.style.overflow = '';
    const el = this.searchOverlayRef?.nativeElement;
    const parent = this.searchOverlayOriginalParent;
    if (el?.parentElement === document.body && parent) {
      parent.appendChild(el);
    }
  }

  changeLanguage(code) {
    if (isPlatformBrowser(this.platformId)) {
      this.translate.use(code);
    }
  }

  get getTotal(): Observable<number> {
    return this.productService.cartTotalAmount();
  }

  get getCantTotal(): Observable<number> {
    return this.productService.cartCantTotal();
  }

  removeItem(product: any) {
    this.productService.removeCartItem(product);
  }

  goToCheckout(event: Event): void {
    event.preventDefault();
    this.cartAuthGate.proceedToCheckout();
  }

  changeCurrency(currency: any) {
    this.productService.Currency = currency;
  }

}
